import React, { useEffect } from 'react';

import {
  PlexInput,
  useDeletePlexMutation,
  useGetPlexLazyQuery,
  useUpdatePlexMutation,
} from '../../../../graphql/gen/types';
import { showPlexSummary } from '../../../../models/filters/actions/showPlexSummary';
import { useFiltersState } from '../../../../models/filters/useFiltersState';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { logAnalytics } from '../../shared/logAnalytics';
import { useDialogState } from '../useDialogState';
import { PlexDialog } from './BasePlexDialog';

type Props = {
  plexId: string;
};

export function EditPlexDialog(props: Props): React.ReactElement {
  const { plexId } = props;
  const [dialogState, setDialogState] = useDialogState();
  const [, dispatch] = useFiltersState();
  const [getPlex, { data }] = useGetPlexLazyQuery({ variables: { plexId } });
  const [updatePlexMutation, { data: updateData, loading }] = useUpdatePlexMutation();
  const [deletePlexMutation] = useDeletePlexMutation();
  const confirmAction = useConfirmAction();
  const open = !!dialogState && dialogState.type === 'edit_plex';

  useEffect(() => {
    // Trigger query once dialog is open
    if (open) {
      getPlex();
    }
  }, [getPlex, open]);

  const onClose = (): void => {
    setDialogState(null);
  };

  const handleUpdatePlex = (plex: PlexInput): void => {
    updatePlexMutation({
      variables: { id: plexId, plex },
    }).then(({ data }) => {
      if (data?.updatePlex.__typename === 'Plex') {
        onClose();
        dispatch(showPlexSummary({ permalink: data.updatePlex.permalink }));
        logAnalytics({ name: 'updated plex', details: { plexId: data?.updatePlex?.id } });
      }
    });
  };

  const handleDeletePlex = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this plex?',
      confirmButton: {
        onClick: (): void => {
          deletePlexMutation({ variables: { plexId } }).then(({ data }) => {
            if (data?.deletePlex != null) {
              onClose();
              logAnalytics({ name: 'deleted plex', details: { plexId } });
            }
          });
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const plex = data?.loggedIn?.plexById;

  return (
    <PlexDialog
      plex={
        plex != null
          ? {
              name: plex.name,
              color: plex.color,
              description: plex.description,
              settings: plex.settings,
              channels: plex.channels,
              googleCalendars: plex.googleCalendars,
              currentUserRelation: plex.currentUserRelation,
            }
          : undefined
      }
      mode="update"
      onSave={handleUpdatePlex}
      saveLoading={loading}
      saveError={updateData?.updatePlex.__typename === 'PlexInputError' ? updateData.updatePlex : null}
      onDelete={handleDeletePlex}
    />
  );
}
